import type { Card, GameState, PlayerId, PlayerState, SlotType } from './types';

/** Joker check — the card is a Joker (either fresh or placed as Double Chance). */
export function isJoker(card: Card | null): boolean {
  return !!card && card.suit === 'joker';
}

/** A placed-on-board "Double Chance" joker. */
export function isDoubleChance(card: Card | null): boolean {
  return !!card && card.isDoubleChance === true;
}

/**
 * Compare two cards. Returns 1 / -1 / 0 (A wins / B wins / tie).
 *
 *   - Ace beats everything except 2.
 *   - 2 beats Ace.
 *   - 2 vs 2 = tie. A vs A = tie.
 *   - Otherwise higher numeric value wins.
 *   - Jokers treated as value 0 if forced into compare (always lose).
 */
export function compareCards(a: Card, b: Card): -1 | 0 | 1 {
  const aIsAce = a.rank === 'A';
  const bIsAce = b.rank === 'A';
  const aIsTwo = a.rank === '2';
  const bIsTwo = b.rank === '2';

  if (aIsAce && bIsTwo) return -1;
  if (aIsTwo && bIsAce) return 1;
  if (aIsAce && bIsAce) return 0;
  if (aIsTwo && bIsTwo) return 0;
  if (aIsAce) return 1;
  if (bIsAce) return -1;

  if (a.value > b.value) return 1;
  if (a.value < b.value) return -1;
  return 0;
}

/** Returns true if drawn defeats target per the Ace/2 rule. */
export function drawnDefeats(drawn: Card, target: Card): boolean {
  return compareCards(drawn, target) > 0;
}

export function otherPlayer(p: PlayerId): PlayerId {
  return p === 'p1' ? 'p2' : 'p1';
}

export function countOccupied(arr: Array<Card | null>): number {
  return arr.filter((c) => c !== null).length;
}

/** Returns the list of empty own slots that can accept the drawn card. */
export function validPlacements(
  player: PlayerState,
  card: Card | null
): Array<{ slotType: SlotType; slotIndex: number }> {
  if (!card || isJoker(card)) return [];
  const out: Array<{ slotType: SlotType; slotIndex: number }> = [];
  player.attacks.forEach((c, i) => { if (c === null) out.push({ slotType: 'attack', slotIndex: i }); });
  player.defenses.forEach((c, i) => { if (c === null) out.push({ slotType: 'defense', slotIndex: i }); });
  if (player.goalkeeper === null) out.push({ slotType: 'goalkeeper', slotIndex: 0 });
  return out;
}

/** Can the player place the drawn card in the given slot? */
export function canPlace(
  player: PlayerState,
  slotType: SlotType,
  slotIndex: number,
  card: Card
): boolean {
  if (!card || isJoker(card)) return false;
  if (slotType === 'attack') return slotIndex >= 0 && slotIndex < 3 && player.attacks[slotIndex] === null;
  if (slotType === 'defense') return slotIndex >= 0 && slotIndex < 2 && player.defenses[slotIndex] === null;
  if (slotType === 'goalkeeper') return slotIndex === 0 && player.goalkeeper === null;
  return false;
}

/**
 * Can the active player attack the given target WITH THEIR DRAWN CARD?
 *
 * Continuous turn system: the drawn card IS the actor (acts as a shot/move).
 *
 * Validation:
 *   - Drawn card must exist and not be a joker.
 *   - Target must be opponent slot with a card.
 *   - Attack progression:
 *       Attack line → Defense line (only if opp.attacks <= 1) → GK (only if opp.attacks <= 1 AND opp.defenses <= 1)
 *   - Drawn card must DEFEAT the target (Ace/2 rule).
 */
export function canAttackWithDrawn(
  state: GameState,
  target: { player: PlayerId; slotType: SlotType; slotIndex: number }
): boolean {
  if (!state.drawnCard) return false;
  if (isJoker(state.drawnCard)) return false;
  if (target.player === state.currentPlayer) return false;
  const opp = state.players[target.player];

  const oppAttacks = countOccupied(opp.attacks);
  const oppDefenses = countOccupied(opp.defenses);

  let targetCard: Card | null = null;
  if (target.slotType === 'attack') {
    targetCard = opp.attacks[target.slotIndex] ?? null;
    if (!targetCard) return false;
    // Double-Chance cards are valid targets regardless of drawn-card rank —
    // the resolution uses the special two-defensive-draw rule.
    if (isDoubleChance(targetCard)) return true;
    return drawnDefeats(state.drawnCard, targetCard);
  } else if (target.slotType === 'defense') {
    if (oppAttacks > 1) return false;
    targetCard = opp.defenses[target.slotIndex] ?? null;
    if (!targetCard) return false;
    if (isDoubleChance(targetCard)) return true;
    return drawnDefeats(state.drawnCard, targetCard);
  } else if (target.slotType === 'goalkeeper') {
    if (oppAttacks > 1) return false;
    if (oppDefenses > 1) return false;
    // Goalkeeper attacks are ALWAYS allowed when the progression rules are met.
    return true;
  }
  return false;
}

/** All targets the drawn card can defeat (per current attack-progression). */
export function validAttackTargets(
  state: GameState
): Array<{ player: PlayerId; slotType: SlotType; slotIndex: number }> {
  if (!state.drawnCard || isJoker(state.drawnCard)) return [];
  const drawn = state.drawnCard;
  const opp = state.players[otherPlayer(state.currentPlayer)];
  const out: Array<{ player: PlayerId; slotType: SlotType; slotIndex: number }> = [];
  const oppAttacks = countOccupied(opp.attacks);
  const oppDefenses = countOccupied(opp.defenses);

  opp.attacks.forEach((c, i) => {
    if (!c) return;
    if (isDoubleChance(c) || drawnDefeats(drawn, c)) {
      out.push({ player: opp.id, slotType: 'attack', slotIndex: i });
    }
  });
  if (oppAttacks <= 1) {
    opp.defenses.forEach((c, i) => {
      if (!c) return;
      if (isDoubleChance(c) || drawnDefeats(drawn, c)) {
        out.push({ player: opp.id, slotType: 'defense', slotIndex: i });
      }
    });
  }
  if (oppAttacks <= 1 && oppDefenses <= 1) {
    out.push({ player: opp.id, slotType: 'goalkeeper', slotIndex: 0 });
  }
  return out;
}

/** Does the drawn card give the current player at least one valid action? */
export function hasAnyValidAction(state: GameState): boolean {
  if (!state.drawnCard) return false;
  // Jokers no longer auto-resolve as a "shot" — they enter joker-choice phase
  // and always have valid actions there (SHOOT is always available).
  if (isJoker(state.drawnCard)) return true;
  const me = state.players[state.currentPlayer];
  if (validPlacements(me, state.drawnCard).length > 0) return true;
  if (validAttackTargets(state).length > 0) return true;
  return false;
}

/** Placements available for a Double Chance joker — atk/def AND goalkeeper. */
export function validDoubleChancePlacements(
  player: PlayerState,
): Array<{ slotType: SlotType; slotIndex: number }> {
  const out: Array<{ slotType: SlotType; slotIndex: number }> = [];
  player.attacks.forEach((c, i) => { if (c === null) out.push({ slotType: 'attack', slotIndex: i }); });
  player.defenses.forEach((c, i) => { if (c === null) out.push({ slotType: 'defense', slotIndex: i }); });
  if (player.goalkeeper === null) out.push({ slotType: 'goalkeeper', slotIndex: 0 });
  return out;
}

/** Targets a Double Chance attack can hit — opponent's attack/defense cards (NOT GK). */
export function validDoubleChanceTargets(
  state: GameState,
): Array<{ player: PlayerId; slotType: 'attack' | 'defense'; slotIndex: number }> {
  const opp = state.players[otherPlayer(state.currentPlayer)];
  const out: Array<{ player: PlayerId; slotType: 'attack' | 'defense'; slotIndex: number }> = [];
  opp.attacks.forEach((c, i) => { if (c) out.push({ player: opp.id, slotType: 'attack', slotIndex: i }); });
  opp.defenses.forEach((c, i) => { if (c) out.push({ player: opp.id, slotType: 'defense', slotIndex: i }); });
  return out;
}

/**
 * Returns true if the current player has at least one legal Double Chance
 * action available (placement in any own slot OR attack on any opp atk/def
 * card). Used by engine + bot to prevent a soft-lock where DOUBLE CHANCE was
 * chosen with no valid follow-up — in that case the engine auto-falls back
 * to SHOOT (2026-02-13 soft-lock fix).
 */
export function hasAnyValidDoubleChanceAction(state: GameState): boolean {
  const me = state.players[state.currentPlayer];
  if (validDoubleChancePlacements(me).length > 0) return true;
  if (validDoubleChanceTargets(state).length > 0) return true;
  return false;
}

